import { useCallback, useEffect, useRef, useState } from "react";
import { interviewService } from "../../services/interviewService";

type StoredInterviewContext = {
    job_title?: string;
    company?: string;
    description?: string;
};

const MAX_QUESTIONS = 7;
const MIN_QUESTIONS = 4;
const MAX_ANSWER_MS = 90_000;
const REALTIME_MODEL = "gpt-4o-realtime-preview-2024-12-17";
const AUDIO_SAMPLE_RATE = 24_000;
const MAX_AUDIO_TAIL_WAIT_MS = 10_000;

type TranscriptEntry = { role: "user" | "ai"; text: string };

function stageInstructionsFor(questionCount: number): string {
    if (questionCount === 0) {
        return (
            "Begin the interview with a professional opening question about the candidate. " +
            "Do NOT say 'welcome' or introduce yourself — just ask the first interview question directly."
        );
    }
    if (questionCount <= 4) {
        return (
            "Ask a question related to the job description, OR based on something the candidate just said. " +
            "If they mentioned companies, projects, roles, years, or metrics, follow up on that. " +
            "Keep it strictly one short interview question."
        );
    }
    if (questionCount === 5) {
        return "Ask about the candidate's salary expectations.";
    }
    return "Ask if the candidate has any questions for the company. Do not answer it; just ask.";
}

function decodePcm16ToFloat32(base64Delta: string): Float32Array {
    const binary = atob(base64Delta);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    const pcm16 = new Int16Array(bytes.buffer);
    const float32 = new Float32Array(pcm16.length);
    for (let i = 0; i < pcm16.length; i++) {
        float32[i] = pcm16[i] / 32768;
    }
    return float32;
}

export function useInterview(onComplete: () => void) {
    const [isListening, setIsListening] = useState(false);
    const [isAISpeaking, setIsAISpeaking] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [questionCount, setQuestionCount] = useState(0);

    const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
    const dataChannelRef = useRef<RTCDataChannel | null>(null);
    const audioContextRef = useRef<AudioContext | null>(null);
    const remoteAudioElRef = useRef<HTMLAudioElement | null>(null);
    const micStreamRef = useRef<MediaStream | null>(null);

    const nextPlaybackTimeRef = useRef(0);
    const autoStopTimerRef = useRef<number | null>(null);
    const audioTailTimerRef = useRef<number | null>(null);
    const firstQuestionSentRef = useRef(false);
    const awaitingAudioTailRef = useRef(false);

    const transcriptRef = useRef<TranscriptEntry[]>([]);
    const [, forceTranscriptTick] = useState(0);
    const aiTextBufferRef = useRef("");
    const userTextBufferRef = useRef("");
    const questionCountRef = useRef(0);

    const appendTranscript = useCallback((entry: TranscriptEntry) => {
        transcriptRef.current = [...transcriptRef.current, entry];
        forceTranscriptTick((n) => n + 1);
    }, []);

    const sendToDataChannel = useCallback((payload: unknown) => {
        const channel = dataChannelRef.current;
        if (channel && channel.readyState === "open") {
            channel.send(JSON.stringify(payload));
        }
    }, []);

    const requestNextQuestion = useCallback(
        (lastAIQuestion: string, lastUserAnswer: string) => {
            const count = questionCountRef.current;

            if (count >= MAX_QUESTIONS) {
                if (count >= MIN_QUESTIONS) {
                    onComplete();
                } else {
                    sendToDataChannel({
                        type: "response.create",
                        response: {
                            instructions:
                                "Continue the interview until at least four questions have been asked. One short question only.",
                        },
                    });
                }
                return;
            }

            sendToDataChannel({
                type: "response.create",
                response: {
                    instructions:
                        "You are the interviewer. ONLY ask questions, never answer them. " +
                        "Never start with 'I...' or roleplay as the candidate. " +
                        `Last AI Question: "${lastAIQuestion}"\n` +
                        `Last User Answer: "${lastUserAnswer}"\n` +
                        stageInstructionsFor(count),
                },
            });
        },
        [onComplete, sendToDataChannel]
    );

    const startMic = useCallback(async () => {
        if (autoStopTimerRef.current) {
            window.clearTimeout(autoStopTimerRef.current);
            autoStopTimerRef.current = null;
        }

        if (!micStreamRef.current) {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            micStreamRef.current = stream;
            stream.getAudioTracks().forEach((track) => {
                peerConnectionRef.current?.addTrack(track, stream);
            });
        }

        micStreamRef.current.getAudioTracks().forEach((track) => {
            track.enabled = true;
        });

        autoStopTimerRef.current = window.setTimeout(stopMicAndAdvance, MAX_ANSWER_MS);
        setIsListening(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const stopMicAndAdvance = useCallback(() => {
        micStreamRef.current?.getAudioTracks().forEach((track) => {
            track.enabled = false;
        });

        if (autoStopTimerRef.current) {
            window.clearTimeout(autoStopTimerRef.current);
            autoStopTimerRef.current = null;
        }

        sendToDataChannel({ type: "input_audio_buffer.commit" });

        let lastUserAnswer = "";
        if (userTextBufferRef.current.trim()) {
            lastUserAnswer = userTextBufferRef.current.trim();
            appendTranscript({ role: "user", text: lastUserAnswer });
            userTextBufferRef.current = "";
        }

        const lastAIQuestion =
            [...transcriptRef.current].reverse().find((entry) => entry.role === "ai")?.text ?? "";

        requestNextQuestion(lastAIQuestion, lastUserAnswer);
        setIsListening(false);
    }, [appendTranscript, requestNextQuestion, sendToDataChannel]);

    const handleVoiceInput = useCallback(() => {
        if (isListening) {
            stopMicAndAdvance();
            return;
        }
        if (!isAISpeaking) {
            void startMic();
        }
    }, [isListening, isAISpeaking, startMic, stopMicAndAdvance]);

    const scheduleAudioChunk = useCallback((base64Delta: string) => {
        const audioContext = audioContextRef.current;
        if (!audioContext) return;

        if (audioContext.state === "suspended") {
            void audioContext.resume();
        }

        const samples = decodePcm16ToFloat32(base64Delta);
        const buffer = audioContext.createBuffer(1, samples.length, AUDIO_SAMPLE_RATE);
        buffer.getChannelData(0).set(samples);

        const source = audioContext.createBufferSource();
        source.buffer = buffer;
        source.connect(audioContext.destination);

        const startAt = Math.max(nextPlaybackTimeRef.current, audioContext.currentTime);
        source.start(startAt);
        nextPlaybackTimeRef.current = startAt + buffer.duration;
    }, []);

    const handleResponseDone = useCallback(() => {
        awaitingAudioTailRef.current = true;
        const audioContext = audioContextRef.current;

        if (audioTailTimerRef.current) {
            window.clearTimeout(audioTailTimerRef.current);
        }

        if (!audioContext) {
            setIsAISpeaking(false);
            return;
        }

        const remainingMs = Math.max(0, (nextPlaybackTimeRef.current - audioContext.currentTime) * 1000);
        const waitMs = Math.min(remainingMs + 200, MAX_AUDIO_TAIL_WAIT_MS);

        audioTailTimerRef.current = window.setTimeout(() => {
            setIsAISpeaking(false);
        }, waitMs);
    }, []);

    const handleDataChannelMessage = useCallback(
        (raw: string) => {
            let msg: { type: string; delta?: string };
            try {
                msg = JSON.parse(raw);
            } catch {
                return;
            }

            switch (msg.type) {
                case "session.updated":
                    setIsLoading(false);
                    break;

                case "response.created":
                    setIsAISpeaking(true);
                    setIsLoading(false);
                    awaitingAudioTailRef.current = false;
                    if (audioTailTimerRef.current) {
                        window.clearTimeout(audioTailTimerRef.current);
                        audioTailTimerRef.current = null;
                    }
                    break;

                case "response.done":
                    handleResponseDone();
                    break;

                case "response.output_audio.delta":
                    if (msg.delta) scheduleAudioChunk(msg.delta);
                    break;

                case "response.output_audio_transcript.delta":
                    if (msg.delta) aiTextBufferRef.current += msg.delta;
                    break;

                case "response.output_audio_transcript.done":
                    if (aiTextBufferRef.current.trim()) {
                        appendTranscript({ role: "ai", text: aiTextBufferRef.current.trim() });
                        questionCountRef.current = Math.min(questionCountRef.current + 1, MAX_QUESTIONS);
                        setQuestionCount(questionCountRef.current);
                        aiTextBufferRef.current = "";
                    }
                    break;

                case "response.input_text.delta":
                    if (msg.delta) userTextBufferRef.current += msg.delta;
                    break;

                case "response.input_text.done":
                    if (userTextBufferRef.current.trim()) {
                        appendTranscript({ role: "user", text: userTextBufferRef.current.trim() });
                        userTextBufferRef.current = "";
                    }
                    break;

                default:
                    break;
            }
        },
        [appendTranscript, handleResponseDone, scheduleAudioChunk]
    );

    // --- AudioContext + hidden playback element lifecycle ---
    useEffect(() => {
        const audioContext = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)({
            sampleRate: AUDIO_SAMPLE_RATE,
        });
        audioContextRef.current = audioContext;

        const audioEl = document.createElement("audio");
        audioEl.autoplay = true;
        audioEl.controls = false;
        audioEl.style.display = "none";
        audioEl.volume = 1.0;
        document.body.appendChild(audioEl);
        audioEl.play().catch(() => {
            /* autoplay blocked until user interaction resumes it below */
        });
        remoteAudioElRef.current = audioEl;

        const resume = () => {
            if (audioContext.state === "suspended") {
                void audioContext.resume();
            }
        };
        resume();

        const interactionEvents: (keyof DocumentEventMap)[] = ["click", "touchstart", "keydown"];
        interactionEvents.forEach((event) => document.addEventListener(event, resume, { once: true }));

        return () => {
            if (audioContext.state !== "closed") audioContext.close();
            audioEl.parentNode?.removeChild(audioEl);
            interactionEvents.forEach((event) => document.removeEventListener(event, resume));
        };
    }, []);

    // --- WebRTC realtime session lifecycle ---
    useEffect(() => {
        let cancelled = false;

        const connect = async () => {
            setIsLoading(true);

            const setupRaw = localStorage.getItem("currentInterviewSetup");
            const interviewSetup: StoredInterviewContext = setupRaw ? JSON.parse(setupRaw) : {};

            try {
                const { ephemeral_token } = await interviewService.getWebRTCSession();
                if (cancelled) return;

                const pc = new RTCPeerConnection({
                    iceServers: [{ urls: ["stun:stun1.l.google.com:19302", "stun:stun2.l.google.com:19302"] }],
                });
                peerConnectionRef.current = pc;

                const micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
                micStreamRef.current = micStream;
                micStream.getAudioTracks().forEach((track) => {
                    track.enabled = false;
                    pc.addTrack(track, micStream);
                });

                const dataChannel = pc.createDataChannel("oai-events", { ordered: true });
                dataChannelRef.current = dataChannel;

                dataChannel.onopen = () => {
                    setIsLoading(false);
                    dataChannel.send(
                        JSON.stringify({
                            type: "session.update",
                            session: {
                                type: "realtime",
                                instructions:
                                    "You are an AI Interviewer. Not a general assistant. " +
                                    "Rules: Only ask interview questions. One short sentence per turn. " +
                                    "Never say 'How may I assist you' or offer help. Never explain or provide answers. " +
                                    `Keep the interview between ${MIN_QUESTIONS} and ${MAX_QUESTIONS} questions; stop after ${MAX_QUESTIONS}. ` +
                                    "Use the candidate's last spoken answer to craft the next question. " +
                                    "If they mention companies, roles, years, projects, or metrics, ask a targeted follow-up. " +
                                    "Do not acknowledge; just ask the next question.\n\n" +
                                    `Context:\nJob Title = ${interviewSetup.job_title}\nCompany = ${interviewSetup.company}\nDescription = ${interviewSetup.description}`,
                                voice: "alloy",
                                input_audio_format: "pcm16",
                                output_audio_format: "pcm16",
                                turn_detection: { type: "none" },
                            },
                        })
                    );

                    const askFirstQuestion = () => {
                        if (firstQuestionSentRef.current) return;
                        firstQuestionSentRef.current = true;
                        dataChannel.send(
                            JSON.stringify({
                                type: "response.create",
                                response: { instructions: "Start the interview now. Ask ONE short question only." },
                            })
                        );
                    };

                    const audioContext = audioContextRef.current;
                    if (audioContext?.state === "suspended") {
                        audioContext.resume().then(askFirstQuestion).catch(askFirstQuestion);
                    } else {
                        askFirstQuestion();
                    }
                };

                dataChannel.onmessage = (event) => handleDataChannelMessage(event.data);
                dataChannel.onerror = () => setIsLoading(false);

                pc.ontrack = (event) => {
                    const [remoteStream] = event.streams;
                    if (remoteAudioElRef.current && remoteStream) {
                        remoteAudioElRef.current.srcObject = remoteStream;
                        remoteAudioElRef.current.play().catch(() => {
                            const retry = () => {
                                remoteAudioElRef.current?.play().catch(() => undefined);
                                document.removeEventListener("click", retry);
                            };
                            document.addEventListener("click", retry, { once: true });
                        });
                    }
                };

                pc.onconnectionstatechange = () => {
                    if (pc.connectionState === "failed" || pc.connectionState === "disconnected") {
                        setIsLoading(false);
                    }
                };

                const offer = await pc.createOffer();
                await pc.setLocalDescription(offer);

                const sdpResponse = await fetch(`https://api.openai.com/v1/realtime?model=${REALTIME_MODEL}`, {
                    method: "POST",
                    body: offer.sdp,
                    headers: {
                        Authorization: `Bearer ${ephemeral_token}`,
                        "Content-Type": "application/sdp",
                    },
                });

                if (!sdpResponse.ok) {
                    throw new Error(`Realtime SDP exchange failed: ${sdpResponse.statusText}`);
                }

                const answerSdp = await sdpResponse.text();
                await pc.setRemoteDescription(new RTCSessionDescription({ type: "answer", sdp: answerSdp }));
            } catch (err) {
                console.error("Failed to establish realtime interview session:", err);
                setIsLoading(false);
            }
        };

        void connect();

        return () => {
            cancelled = true;
            peerConnectionRef.current?.close();
            micStreamRef.current?.getTracks().forEach((track) => track.stop());
            if (autoStopTimerRef.current) window.clearTimeout(autoStopTimerRef.current);
            if (audioTailTimerRef.current) window.clearTimeout(audioTailTimerRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handleDataChannelMessage]);

    return {
        isListening,
        isAISpeaking,
        isLoading,
        questionCount,
        handleVoiceInput,
    };
}
